import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import { CategoryBlockTitle } from '../shared/CategoryBlockTitle';
import { createCategoryUrl } from '../shared/utils';

interface CategoriesTilesThreeLevelBlockProps {
  category: Category;
}

const CategoriesTilesThreeLevelBlock = ({
  category,
}: CategoriesTilesThreeLevelBlockProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { handleClickCategory } = useCategoryAnalytics();

  const items = category.subcategory.flatMap((c) => c.subcategory).slice(0, 9);

  if (!items.length) {
    return null;
  }

  const rows = [items.slice(0, 3), items.slice(3, 6), items.slice(6, 9)].filter(
    (row) => row.length > 0,
  );

  const ViewAllComponent = isMobile
    ? Typography.Text100Medium
    : Typography.Text200Medium;

  const CardTitleComponent = isMobile
    ? Typography.Header150Medium
    : Typography.Header300Medium;

  const renderCard = (item: Category['subcategory'][number]) => (
    <Link
      to={createCategoryUrl({ id: item.id })}
      onClick={handleClickCategory(item.id)}
      data-link={`category-${item.id}`}
      className="relative flex aspect-228/169 w-full overflow-hidden rounded-5.5 no-underline md:rounded-6.5"
    >
      <CloudflareResponseImage
        src={item.previewImage || ''}
        alt={item.name}
        className="absolute inset-0 size-full object-cover"
        loading="lazy"
        srcSetParams={defaultSrcSetParams}
      />
      <div className="absolute inset-0 bg-linear-to-b from-black/20 to-black/40" />
      <div className="absolute right-4 bottom-4 left-4 md:right-9 md:bottom-7 md:left-7">
        <CardTitleComponent
          as="span"
          className="wrap-anywhere block text-text-primary-inverse!"
        >
          {item.name}
        </CardTitleComponent>
      </div>
    </Link>
  );

  const viewAllLinkTo =
    pathname !== '/search' ? createCategoryUrl({ id: category.id }) : undefined;

  return (
    <section className="flex w-full flex-col gap-4 md:gap-8">
      <header className="flex w-full items-center gap-3 md:gap-4">
        <CategoryBlockTitle className="wrap-anywhere flex-1">
          {category.name}
        </CategoryBlockTitle>

        {viewAllLinkTo && (
          <Link
            to={viewAllLinkTo}
            className={cn(
              'inline-flex h-8 shrink-0 items-center rounded-full border border-stroke-primary px-3 text-text-primary no-underline transition-colors',
              'hover:bg-surface-transparent-2 hover:text-text-primary hover:no-underline',
              'md:h-10 md:px-4',
            )}
            onClick={handleClickCategory(category.id)}
          >
            <ViewAllComponent as="span" className="block">
              {t('viewAll')}
            </ViewAllComponent>
          </Link>
        )}
      </header>

      {!isMobile && (
        <div className="grid w-full grid-cols-3 gap-4">
          {items.map((item) => (
            <Fragment key={item.id}>{renderCard(item)}</Fragment>
          ))}
        </div>
      )}

      {isMobile && (
        <div className="flex w-full flex-col gap-4">
          {rows.map((row, rowIndex) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: rows are derived from a stable slice of fixed length 3
              key={rowIndex}
              className={cn(
                '-mx-4 flex max-w-[calc(100%+32px)] gap-2 overflow-x-auto overflow-y-hidden px-4 pb-2',
                '[-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [scrollbar-width:none]',
                '[&::-webkit-scrollbar]:hidden',
              )}
            >
              {row.map((item) => (
                <div key={item.id} className="flex w-70 shrink-0">
                  {renderCard(item)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default observer(CategoriesTilesThreeLevelBlock);
